/**
 * sl-pmcz.2.1: AI Personalization Settings — User Memory Management
 *
 * Allows users to view, edit, delete, and manually create AI memories.
 * Memories are user-scoped (workspace_memories table, userId filter).
 * Only visible when workspace enablePersonalMemory is active.
 */
import {
  Button,
  Menu,
  MenuItem,
  MenuTrigger,
  notify,
  useConfirmModal,
} from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { ServerService, workspaceApiFetch } from '@affine/core/modules/cloud';
import { useI18n } from '@affine/i18n';
import {
  DeleteIcon,
  EditIcon,
  FavoritedIcon,
  FavoriteIcon,
  PlusIcon,
} from '@blocksuite/icons/rc';
import { useFramework } from '@toeverything/infra';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';

interface UserMemory {
  id: string;
  content: string;
  category: string;
  importance: number;
  effectiveImportance: number;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TelegramConnectionStatus {
  id: string | null;
  enabled: boolean;
  botConfigured?: boolean;
  botUsername: string | null;
  chatBound: boolean;
  startUrl: string | null;
  status: string | null;
  lastError: string | null;
}

interface TemporaryAssistantGrant {
  id: string;
  surface: string;
  threadId: string;
  capability: string;
  targetRef: string | null;
  expiresAt: string;
  maxExecutions: number;
  usedExecutions: number;
}

const CATEGORY_LABELS: Record<string, { en: string; de: string }> = {
  preference: { en: 'Preference', de: 'Präferenz' },
  fact: { en: 'Fact', de: 'Fakt' },
  workflow: { en: 'Workflow', de: 'Workflow' },
  domain: { en: 'Domain', de: 'Domäne' },
  relationship: { en: 'Relationship', de: 'Beziehung' },
};

// Category pill colors — mid-saturation that contrast on both light and dark backgrounds.
// Text = saturated color, Background = same color at 15% alpha.
const CATEGORY_STYLES: Record<string, { bg: string; fg: string }> = {
  preference: { fg: '#a855f7', bg: 'rgba(168,85,247,0.15)' },
  fact: { fg: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
  workflow: { fg: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  domain: { fg: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  relationship: { fg: '#ec4899', bg: 'rgba(236,72,153,0.15)' },
};

const menuTriggerStyle: CSSProperties = {
  width: '100%',
  minWidth: 160,
  fontWeight: 500,
};

const MemoryCategorySelect = ({
  value,
  onChange,
  lang,
  includeAll = false,
  style,
}: {
  value: string;
  onChange: (value: string) => void;
  lang: 'de' | 'en';
  includeAll?: boolean;
  style?: CSSProperties;
}) => {
  const options = [
    ...(includeAll
      ? [{ key: 'all', label: lang === 'de' ? 'Alle' : 'All' }]
      : []),
    ...Object.entries(CATEGORY_LABELS).map(([key, label]) => ({
      key,
      label: label[lang],
    })),
  ];
  const selected = options.find(option => option.key === value);

  return (
    <Menu
      items={options.map(option => (
        <MenuItem
          key={option.key}
          selected={option.key === value}
          onSelect={() => onChange(option.key)}
        >
          {option.label}
        </MenuItem>
      ))}
      contentOptions={{ align: 'start', style: { minWidth: 180 } }}
    >
      <MenuTrigger block style={{ ...menuTriggerStyle, ...style }}>
        {selected?.label ?? '—'}
      </MenuTrigger>
    </Menu>
  );
};

const EditMemoryFields = ({
  memory,
  lang,
  onContentChange,
  onCategoryChange,
}: {
  memory: UserMemory;
  lang: 'de' | 'en';
  onContentChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
}) => {
  const [category, setCategory] = useState(memory.category);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginTop: 8,
      }}
    >
      <div>
        <label
          style={{
            fontSize: 12,
            color: 'var(--streamlain-text-secondary-color)',
            display: 'block',
            marginBottom: 4,
          }}
        >
          {lang === 'de' ? 'Inhalt' : 'Content'}
        </label>
        <textarea
          defaultValue={memory.content}
          onChange={e => onContentChange(e.target.value)}
          rows={3}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: 4,
            border: '1px solid var(--streamlain-border-color)',
            backgroundColor: 'var(--streamlain-background-primary-color)',
            color: 'var(--streamlain-text-primary-color)',
            fontSize: 13,
            resize: 'vertical',
            outline: 'none',
          }}
        />
      </div>
      <div>
        <label
          style={{
            fontSize: 12,
            color: 'var(--streamlain-text-secondary-color)',
            display: 'block',
            marginBottom: 4,
          }}
        >
          {lang === 'de' ? 'Kategorie' : 'Category'}
        </label>
        <MemoryCategorySelect
          value={category}
          lang={lang}
          onChange={value => {
            setCategory(value);
            onCategoryChange(value);
          }}
        />
      </div>
    </div>
  );
};

interface AIPersonalizationSettingsProps {
  showHeader?: boolean;
  showMemories?: boolean;
  showTelegram?: boolean;
  showTemporaryGrants?: boolean;
}

export const AIPersonalizationSettings = ({
  showHeader = true,
  showMemories = true,
  showTelegram = true,
  showTemporaryGrants = true,
}: AIPersonalizationSettingsProps = {}) => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const framework = useFramework();
  const [memories, setMemories] = useState<UserMemory[]>([]);
  const [loading, setLoading] = useState(true);
  const { openConfirmModal } = useConfirmModal();
  const editContentRef = useRef('');
  const editCategoryRef = useRef('fact');
  const [showAdd, setShowAdd] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState('fact');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [usage, setUsage] = useState<{ count: number; limit: number }>({
    count: 0,
    limit: 100,
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [telegramStatus, setTelegramStatus] =
    useState<TelegramConnectionStatus | null>(null);
  const [telegramMutating, setTelegramMutating] = useState(false);
  const [temporaryGrants, setTemporaryGrants] = useState<
    TemporaryAssistantGrant[]
  >([]);
  const [grantMutatingId, setGrantMutatingId] = useState<string | null>(null);

  const lang = (navigator.language || 'en').startsWith('de') ? 'de' : 'en';
  const usagePercent = usage.limit > 0 ? (usage.count / usage.limit) * 100 : 0;

  const filteredMemories = useMemo(
    () =>
      memories.filter(m => {
        if (filterCategory !== 'all' && m.category !== filterCategory)
          return false;
        if (
          searchQuery &&
          !m.content.toLowerCase().includes(searchQuery.toLowerCase())
        )
          return false;
        return true;
      }),
    [memories, filterCategory, searchQuery]
  );

  const getWorkspaceId = useCallback(() => {
    const wsMatch = location.pathname.match(/\/workspace\/([^/]+)/);
    return wsMatch?.[1] ?? '';
  }, []);

  const fetchMemories = useCallback(async () => {
    const workspaceId = getWorkspaceId();
    if (!workspaceId || !backendAvailable) {
      setLoading(false);
      return;
    }
    const serverService = framework.get(ServerService);
    const server = serverService.server;

    try {
      const res: any = await server.gql({
        query: {
          id: 'userMemoriesQuery',
          query: `query userMemories($workspaceId: String!) {
            userMemories(workspaceId: $workspaceId) {
              id content category importance effectiveImportance pinned createdAt updatedAt
            }
            memoryUsage(workspaceId: $workspaceId) { count limit }
          }`,
          operationName: 'userMemories',
          definitionName: 'userMemories',
        } as any,
        variables: { workspaceId } as any,
      });
      setMemories(res?.userMemories ?? []);
      if (res?.memoryUsage) {
        setUsage(res.memoryUsage);
      }
    } catch {
      setMemories([]);
    } finally {
      setLoading(false);
    }
  }, [backendAvailable, framework, getWorkspaceId]);

  const fetchTelegramStatus = useCallback(async () => {
    const workspaceId = getWorkspaceId();
    if (!workspaceId || !backendAvailable) return;
    try {
      const res = await workspaceApiFetch(
        `/api/workspace/${workspaceId}/assistant/telegram/me`,
        {
          credentials: 'include',
        }
      );
      if (!res.ok) return;
      setTelegramStatus((await res.json()) as TelegramConnectionStatus);
    } catch {
      setTelegramStatus(null);
    }
  }, [backendAvailable, getWorkspaceId]);

  const fetchTemporaryGrants = useCallback(async () => {
    const workspaceId = getWorkspaceId();
    if (!workspaceId || !backendAvailable) return;
    try {
      const res = await workspaceApiFetch(
        `/api/workspace/${workspaceId}/assistant/me/grants`,
        {
          credentials: 'include',
        }
      );
      if (!res.ok) return;
      const body = (await res.json()) as { grants?: TemporaryAssistantGrant[] };
      setTemporaryGrants(body.grants ?? []);
    } catch {
      setTemporaryGrants([]);
    }
  }, [backendAvailable, getWorkspaceId]);

  useEffect(() => {
    if (showMemories) {
      void fetchMemories();
    } else {
      setMemories([]);
      setUsage({ count: 0, limit: 0 });
      setLoading(false);
    }

    if (showTelegram) {
      void fetchTelegramStatus();
    } else {
      setTelegramStatus(null);
    }

    if (showTemporaryGrants) {
      void fetchTemporaryGrants();
    } else {
      setTemporaryGrants([]);
    }
  }, [
    fetchMemories,
    fetchTelegramStatus,
    fetchTemporaryGrants,
    showMemories,
    showTelegram,
    showTemporaryGrants,
  ]);

  const handleConnectTelegram = useCallback(async () => {
    const workspaceId = getWorkspaceId();
    if (!workspaceId) return;
    setTelegramMutating(true);
    try {
      const res = await workspaceApiFetch(
        `/api/workspace/${workspaceId}/assistant/telegram/me`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? 'Failed to connect Telegram');
      }
      setTelegramStatus((await res.json()) as TelegramConnectionStatus);
      notify.success({
        title:
          t['com.streamlain.ai-personalization.telegram-connected-toast']?.() ??
          (lang === 'de'
            ? 'Telegram Assistant verbunden'
            : 'Telegram assistant connected'),
      });
    } catch (error) {
      notify.error({
        title:
          error instanceof Error
            ? error.message
            : (t[
                'com.streamlain.ai-personalization.telegram-connect-failed'
              ]?.() ??
              (lang === 'de'
                ? 'Telegram konnte nicht verbunden werden'
                : 'Could not connect Telegram')),
      });
    } finally {
      setTelegramMutating(false);
    }
  }, [getWorkspaceId, lang, t]);

  const handleDisconnectTelegram = useCallback(async () => {
    const workspaceId = getWorkspaceId();
    if (!workspaceId) return;
    setTelegramMutating(true);
    try {
      const res = await workspaceApiFetch(
        `/api/workspace/${workspaceId}/assistant/telegram/me`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to disconnect Telegram');
      setTelegramStatus((await res.json()) as TelegramConnectionStatus);
      notify.success({
        title:
          t[
            'com.streamlain.ai-personalization.telegram-disconnected-toast'
          ]?.() ??
          (lang === 'de'
            ? 'Telegram Assistant getrennt'
            : 'Telegram assistant disconnected'),
      });
    } catch {
      notify.error({
        title:
          t[
            'com.streamlain.ai-personalization.telegram-disconnect-failed'
          ]?.() ??
          (lang === 'de'
            ? 'Telegram konnte nicht getrennt werden'
            : 'Could not disconnect Telegram'),
      });
    } finally {
      setTelegramMutating(false);
    }
  }, [getWorkspaceId, lang, t]);

  const handleRevokeTemporaryGrant = useCallback(
    async (grantId: string) => {
      const workspaceId = getWorkspaceId();
      if (!workspaceId) return;
      setGrantMutatingId(grantId);
      try {
        const res = await workspaceApiFetch(
          `/api/workspace/${workspaceId}/assistant/me/grants/${grantId}`,
          { method: 'DELETE', credentials: 'include' }
        );
        if (!res.ok) throw new Error('Failed to revoke temporary grant');
        setTemporaryGrants(prev => prev.filter(grant => grant.id !== grantId));
        notify.success({
          title:
            lang === 'de' ? 'Freigabe widerrufen' : 'Temporary grant revoked',
        });
      } catch {
        notify.error({
          title:
            lang === 'de'
              ? 'Freigabe konnte nicht widerrufen werden'
              : 'Could not revoke temporary grant',
        });
      } finally {
        setGrantMutatingId(null);
      }
    },
    [getWorkspaceId, lang]
  );

  const handleDelete = useCallback(
    (id: string) => {
      openConfirmModal({
        title:
          t['com.streamlain.ai-personalization.delete-confirm-title']?.() ??
          'Delete memory?',
        description:
          t[
            'com.streamlain.ai-personalization.delete-confirm-description'
          ]?.() ?? 'This memory will be permanently deleted.',
        confirmText:
          t['com.streamlain.ai-personalization.delete']?.() ?? 'Delete',
        confirmButtonOptions: {
          variant: 'error',
        },
        onConfirm: async () => {
          const serverService = framework.get(ServerService);
          const server = serverService.server;
          try {
            await server.gql({
              query: {
                id: 'deleteUserMemoryMutation',
                query: `mutation deleteUserMemory($id: String!) {
                  deleteUserMemory(id: $id)
                }`,
                operationName: 'deleteUserMemory',
                definitionName: 'deleteUserMemory',
              } as any,
              variables: { id } as any,
            });
            setMemories(prev => prev.filter(m => m.id !== id));
            setUsage(prev => ({
              ...prev,
              count: Math.max(0, prev.count - 1),
            }));
            notify.success({
              title:
                t['com.streamlain.ai-personalization.deleted']?.() ??
                'Memory deleted',
            });
          } catch {
            notify.error({
              title: 'Failed to delete memory',
            });
          }
        },
      });
    },
    [framework, t, openConfirmModal]
  );

  const handleSaveEdit = useCallback(
    async (id: string) => {
      const content = editContentRef.current.trim();
      const category = editCategoryRef.current;
      if (!content) return;
      const serverService = framework.get(ServerService);
      const server = serverService.server;
      try {
        const res: any = await server.gql({
          query: {
            id: 'updateUserMemoryMutation',
            query: `mutation updateUserMemory($id: String!, $content: String!, $category: String) {
              updateUserMemory(id: $id, content: $content, category: $category) {
                id content category importance effectiveImportance pinned createdAt updatedAt
              }
            }`,
            operationName: 'updateUserMemory',
            definitionName: 'updateUserMemory',
          } as any,
          variables: { id, content, category } as any,
        });
        const updated = res?.updateUserMemory;
        if (updated) {
          setMemories(prev => prev.map(m => (m.id === id ? updated : m)));
        }
      } catch {
        notify.error({ title: 'Failed to update memory' });
      }
    },
    [framework]
  );

  const handleCreate = useCallback(async () => {
    if (!newContent.trim()) return;
    const workspaceId = getWorkspaceId();
    if (!workspaceId) return;
    const serverService = framework.get(ServerService);
    const server = serverService.server;
    try {
      const res: any = await server.gql({
        query: {
          id: 'createUserMemoryMutation',
          query: `mutation createUserMemory($workspaceId: String!, $content: String!, $category: String!) {
            createUserMemory(workspaceId: $workspaceId, content: $content, category: $category) {
              id content category importance createdAt updatedAt
            }
          }`,
          operationName: 'createUserMemory',
          definitionName: 'createUserMemory',
        } as any,
        variables: {
          workspaceId,
          content: newContent,
          category: newCategory,
        } as any,
      });
      const created = res?.createUserMemory;
      if (created) {
        setMemories(prev => [created, ...prev]);
        setUsage(prev => ({ ...prev, count: prev.count + 1 }));
      }
      setNewContent('');
      setShowAdd(false);
      notify.success({
        title:
          t['com.streamlain.ai-personalization.created']?.() ??
          'Memory created',
      });
    } catch {
      notify.error({ title: 'Failed to create memory' });
    }
  }, [newContent, newCategory, framework, getWorkspaceId, t]);

  const handlePin = useCallback(
    async (id: string, pinned: boolean) => {
      const serverService = framework.get(ServerService);
      const server = serverService.server;
      try {
        const res: any = await server.gql({
          query: {
            id: 'pinUserMemoryMutation',
            query: `mutation pinUserMemory($id: String!, $pinned: Boolean!) {
              pinUserMemory(id: $id, pinned: $pinned) {
                id content category importance effectiveImportance pinned createdAt updatedAt
              }
            }`,
            operationName: 'pinUserMemory',
            definitionName: 'pinUserMemory',
          } as any,
          variables: { id, pinned } as any,
        });
        const updated = res?.pinUserMemory;
        if (updated) {
          setMemories(prev => prev.map(m => (m.id === id ? updated : m)));
        }
      } catch {
        notify.error({ title: 'Failed to pin memory' });
      }
    },
    [framework]
  );

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    const l = (navigator.language || 'en').startsWith('de') ? 'de' : 'en';
    openConfirmModal({
      title:
        l === 'de'
          ? `${count} Erinnerungen loeschen?`
          : `Delete ${count} memories?`,
      description:
        l === 'de'
          ? 'Diese Erinnerungen werden unwiderruflich geloescht.'
          : 'These memories will be permanently deleted.',
      confirmText: l === 'de' ? 'Loeschen' : 'Delete',
      confirmButtonOptions: { variant: 'error' },
      onConfirm: async () => {
        const serverService = framework.get(ServerService);
        const server = serverService.server;
        const ids = [...selectedIds];
        let deleted = 0;
        for (const id of ids) {
          try {
            await server.gql({
              query: {
                id: 'deleteUserMemoryMutation',
                query: `mutation deleteUserMemory($id: String!) { deleteUserMemory(id: $id) }`,
                operationName: 'deleteUserMemory',
                definitionName: 'deleteUserMemory',
              } as any,
              variables: { id } as any,
            });
            deleted++;
          } catch {
            // continue with remaining
          }
        }
        setMemories(prev => prev.filter(m => !selectedIds.has(m.id)));
        setUsage(prev => ({
          ...prev,
          count: Math.max(0, prev.count - deleted),
        }));
        setSelectedIds(new Set());
        notify.success({
          title:
            l === 'de'
              ? `${deleted} Erinnerungen geloescht`
              : `${deleted} memories deleted`,
        });
      },
    });
  }, [selectedIds, framework, openConfirmModal]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredMemories.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMemories.map(m => m.id)));
    }
  }, [selectedIds.size, filteredMemories]);

  const openEditDialog = useCallback(
    (memory: UserMemory) => {
      // Set refs so handleSaveEdit reads the latest values
      editContentRef.current = memory.content;
      editCategoryRef.current = memory.category;

      const l = (navigator.language || 'en').startsWith('de') ? 'de' : 'en';
      openConfirmModal({
        title: l === 'de' ? 'Erinnerung bearbeiten' : 'Edit memory',
        description: (
          <EditMemoryFields
            memory={memory}
            lang={l}
            onContentChange={value => {
              editContentRef.current = value;
            }}
            onCategoryChange={value => {
              editCategoryRef.current = value;
            }}
          />
        ),
        confirmText: l === 'de' ? 'Speichern' : 'Save',
        cancelText: l === 'de' ? 'Abbrechen' : 'Cancel',
        onConfirm: () => void handleSaveEdit(memory.id),
      });
    },
    [openConfirmModal, handleSaveEdit]
  );

  return (
    <>
      {showHeader ? (
        <SettingHeader
          title={
            t['com.streamlain.ai-personalization.title']?.() ??
            'AI Personalization'
          }
          subtitle={
            t['com.streamlain.ai-personalization.subtitle']?.() ??
            'Manage what your AI assistant remembers about you'
          }
          action={<SettingsOfflineHeaderAction show={!backendAvailable} />}
        />
      ) : null}

      {showTelegram ? (
        <SettingWrapper
          title={
            t['com.streamlain.ai-personalization.telegram-title']?.() ??
            'Telegram Assistant'
          }
        >
          <SettingRow
            name={
              t['com.streamlain.ai-personalization.telegram-status']?.() ??
              'Personal Telegram bot'
            }
            desc={
              telegramStatus?.enabled === false
                ? (t[
                    'com.streamlain.ai-personalization.telegram-disabled'
                  ]?.() ??
                  'Your workspace admin disabled Telegram access for your assistant.')
                : telegramStatus?.botConfigured === false
                  ? (t[
                      'com.streamlain.ai-personalization.telegram-admin-required'
                    ]?.() ??
                    (lang === 'de'
                      ? 'Dein Workspace-Admin muss Telegram zuerst fuer diesen Workspace einrichten.'
                      : 'Your workspace admin must set up Telegram for this workspace first.'))
                  : telegramStatus?.id
                    ? telegramStatus.chatBound
                      ? (t[
                          'com.streamlain.ai-personalization.telegram-connected'
                        ]?.({ username: telegramStatus.botUsername ?? '' }) ??
                        (lang === 'de'
                          ? `Verbunden mit @${telegramStatus.botUsername}`
                          : `Connected to @${telegramStatus.botUsername}`))
                      : (t[
                          'com.streamlain.ai-personalization.telegram-start-private'
                        ]?.({ username: telegramStatus.botUsername ?? '' }) ??
                        (lang === 'de'
                          ? `Öffne den Setup-Link und starte @${telegramStatus.botUsername} privat.`
                          : `Open the setup link and start @${telegramStatus.botUsername} privately.`))
                    : (t[
                        'com.streamlain.ai-personalization.telegram-description'
                      ]?.() ??
                      'Connect Telegram to continue this assistant from Telegram.')
            }
          >
            {telegramStatus?.id ? (
              <div style={{ display: 'flex', gap: 8 }}>
                {telegramStatus.startUrl && !telegramStatus.chatBound ? (
                  <Button
                    variant="primary"
                    onClick={() =>
                      window.open(telegramStatus.startUrl!, '_blank')
                    }
                  >
                    {t['com.streamlain.ai-personalization.telegram-open']?.() ??
                      (lang === 'de' ? 'Telegram öffnen' : 'Open Telegram')}
                  </Button>
                ) : null}
                <Button
                  variant="plain"
                  disabled={telegramMutating}
                  onClick={() => void handleDisconnectTelegram()}
                >
                  {t[
                    'com.streamlain.ai-personalization.telegram-disconnect'
                  ]?.() ?? 'Disconnect'}
                </Button>
              </div>
            ) : null}
          </SettingRow>

          {telegramStatus &&
          telegramStatus.enabled !== false &&
          telegramStatus.botConfigured !== false &&
          !telegramStatus.id ? (
            <div
              style={{
                padding: '0 0 12px',
                display: 'flex',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <Button
                variant="primary"
                disabled={telegramMutating}
                onClick={() => void handleConnectTelegram()}
              >
                {t['com.streamlain.ai-personalization.telegram-connect']?.() ??
                  'Connect'}
              </Button>
            </div>
          ) : null}

          {telegramStatus?.lastError ? (
            <div
              style={{
                paddingBottom: 12,
                color: 'var(--streamlain-error-color)',
                fontSize: 12,
                lineHeight: '18px',
              }}
            >
              {telegramStatus.lastError}
            </div>
          ) : null}
        </SettingWrapper>
      ) : null}

      {showTemporaryGrants && temporaryGrants.length > 0 ? (
        <SettingWrapper
          title={
            t['com.streamlain.ai-personalization.grants-title']?.() ??
            (lang === 'de'
              ? 'Zeitbegrenzte KI-Freigaben'
              : 'Temporary AI grants')
          }
        >
          <div style={{ padding: '4px 0 12px' }}>
            {temporaryGrants.map(grant => (
              <div
                key={grant.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 0',
                  borderTop: '1px solid var(--streamlain-border-color)',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      color: 'var(--streamlain-text-primary-color)',
                      fontSize: 13,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {grant.capability} · {grant.surface}
                  </div>
                  <div
                    style={{
                      color: 'var(--streamlain-text-secondary-color)',
                      fontSize: 12,
                      lineHeight: '18px',
                    }}
                  >
                    {lang === 'de' ? 'Bis' : 'Until'}{' '}
                    {new Date(grant.expiresAt).toLocaleString(
                      lang === 'de' ? 'de-DE' : 'en-US'
                    )}
                    {' · '}
                    {grant.maxExecutions - grant.usedExecutions}{' '}
                    {lang === 'de' ? 'Ausführungen übrig' : 'executions left'}
                  </div>
                </div>
                <Button
                  variant="plain"
                  disabled={grantMutatingId === grant.id}
                  onClick={() => void handleRevokeTemporaryGrant(grant.id)}
                >
                  {lang === 'de' ? 'Widerrufen' : 'Revoke'}
                </Button>
              </div>
            ))}
          </div>
        </SettingWrapper>
      ) : null}

      {showMemories ? (
        <>
          <SettingWrapper
            title={
              t['com.streamlain.ai-personalization.memories-title']?.() ??
              'Memories'
            }
          >
            {/* Add memory button */}
            <SettingRow
              name={
                t['com.streamlain.ai-personalization.add-memory']?.() ??
                'Add memory'
              }
              desc={
                t['com.streamlain.ai-personalization.add-memory-desc']?.() ??
                'Tell your assistant something to remember'
              }
            >
              <Button
                variant="primary"
                prefix={<PlusIcon />}
                onClick={() => setShowAdd(!showAdd)}
                disabled={!backendAvailable}
              >
                {t['com.streamlain.ai-personalization.add']?.() ?? 'Add'}
              </Button>
            </SettingRow>

            {/* Add form */}
            {showAdd && (
              <div
                style={{
                  padding: '0 0 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <input
                  placeholder={
                    t[
                      'com.streamlain.ai-personalization.add-placeholder'
                    ]?.() ?? 'e.g. I prefer concise answers'
                  }
                  value={newContent}
                  onChange={e => setNewContent(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') void handleCreate();
                  }}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    borderRadius: 4,
                    border: '1px solid var(--streamlain-border-color)',
                    backgroundColor:
                      'var(--streamlain-background-primary-color)',
                    color: 'var(--streamlain-text-primary-color)',
                    fontSize: 13,
                    outline: 'none',
                  }}
                />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <MemoryCategorySelect
                    value={newCategory}
                    onChange={setNewCategory}
                    lang={lang}
                    style={{ width: 180 }}
                  />
                  <Button variant="primary" onClick={() => void handleCreate()}>
                    {t['com.streamlain.ai-personalization.save']?.() ?? 'Save'}
                  </Button>
                  <Button variant="plain" onClick={() => setShowAdd(false)}>
                    {t['Cancel']?.() ?? 'Cancel'}
                  </Button>
                </div>
              </div>
            )}

            {/* Usage bar */}
            <div
              style={{
                padding: '4px 0 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'var(--streamlain-hover-color)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(usagePercent, 100)}%`,
                      borderRadius: 3,
                      backgroundColor:
                        usagePercent > 90
                          ? '#ef4444'
                          : usagePercent > 70
                            ? '#f59e0b'
                            : '#3b82f6',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--streamlain-text-secondary-color)',
                  whiteSpace: 'nowrap',
                }}
              >
                {usage.count} / {usage.limit}
              </span>
            </div>

            {/* Search + Category filter */}
            {memories.length > 0 && (
              <div style={{ padding: '0 0 8px', display: 'flex', gap: 8 }}>
                <input
                  placeholder={
                    lang === 'de'
                      ? 'Erinnerungen durchsuchen...'
                      : 'Search memories...'
                  }
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    borderRadius: 4,
                    border: '1px solid var(--streamlain-border-color)',
                    backgroundColor:
                      'var(--streamlain-background-primary-color)',
                    color: 'var(--streamlain-text-primary-color)',
                    fontSize: 13,
                    outline: 'none',
                  }}
                />
                <MemoryCategorySelect
                  value={filterCategory}
                  onChange={setFilterCategory}
                  lang={lang}
                  includeAll
                  style={{ width: 180 }}
                />
              </div>
            )}

            {/* Multi-select bar */}
            {memories.length > 0 && (
              <div
                style={{
                  padding: '6px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  borderBottom: '1px solid var(--streamlain-border-color)',
                }}
              >
                <div
                  onClick={toggleSelectAll}
                  title={lang === 'de' ? 'Alle auswaehlen' : 'Select all'}
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 3,
                    border: `1.5px solid ${filteredMemories.length > 0 && selectedIds.size === filteredMemories.length ? 'var(--streamlain-primary-color)' : 'var(--streamlain-border-color)'}`,
                    backgroundColor:
                      filteredMemories.length > 0 &&
                      selectedIds.size === filteredMemories.length
                        ? 'var(--streamlain-primary-color)'
                        : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {filteredMemories.length > 0 &&
                    selectedIds.size === filteredMemories.length && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path
                          d="M1 4L3.5 6.5L9 1"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                </div>
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--streamlain-text-secondary-color)',
                    flex: 1,
                  }}
                >
                  {selectedIds.size > 0
                    ? lang === 'de'
                      ? `${selectedIds.size} ausgewaehlt`
                      : `${selectedIds.size} selected`
                    : lang === 'de'
                      ? 'Auswaehlen'
                      : 'Select'}
                </span>
                {selectedIds.size > 0 && (
                  <Button
                    variant="error"
                    onClick={handleBulkDelete}
                    style={{ fontSize: 12, padding: '2px 10px' }}
                  >
                    <DeleteIcon
                      style={{ width: 14, height: 14, marginRight: 4 }}
                    />
                    {lang === 'de'
                      ? `${selectedIds.size} loeschen`
                      : `Delete ${selectedIds.size}`}
                  </Button>
                )}
              </div>
            )}

            {/* Memory list */}
            {loading ? (
              <div
                style={{
                  padding: '16px 0',
                  color: 'var(--streamlain-text-secondary-color)',
                }}
              >
                {backendAvailable ? 'Loading...' : offlineLabel}
              </div>
            ) : memories.length === 0 ? (
              <div
                style={{
                  padding: '24px 0',
                  textAlign: 'center',
                  color: 'var(--streamlain-text-secondary-color)',
                  fontSize: 14,
                }}
              >
                {backendAvailable
                  ? (t['com.streamlain.ai-personalization.empty']?.() ??
                    'No memories yet. Your AI assistant will learn about you as you chat.')
                  : offlineLabel}
              </div>
            ) : filteredMemories.length === 0 ? (
              <div
                style={{
                  padding: '16px 0',
                  textAlign: 'center',
                  color: 'var(--streamlain-text-secondary-color)',
                  fontSize: 13,
                }}
              >
                {lang === 'de' ? 'Keine Treffer' : 'No matches'}
              </div>
            ) : (
              filteredMemories.map(memory => {
                const effImp = memory.effectiveImportance;
                const effImpRounded = Math.round(effImp * 10) / 10;
                return (
                  <div
                    key={memory.id}
                    style={{
                      padding: '10px 0',
                      borderBottom: '1px solid var(--streamlain-border-color)',
                      display: 'grid',
                      gridTemplateColumns: '24px 64px 1fr auto',
                      gap: '0 12px',
                      alignItems: 'start',
                      backgroundColor: selectedIds.has(memory.id)
                        ? 'var(--streamlain-hover-color)'
                        : undefined,
                    }}
                  >
                    {/* Col 0: Checkbox */}
                    <div
                      onClick={() => toggleSelect(memory.id)}
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 3,
                        border: `1.5px solid ${selectedIds.has(memory.id) ? 'var(--streamlain-primary-color)' : 'var(--streamlain-border-color)'}`,
                        backgroundColor: selectedIds.has(memory.id)
                          ? 'var(--streamlain-primary-color)'
                          : 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        alignSelf: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {selectedIds.has(memory.id) && (
                        <svg
                          width="10"
                          height="8"
                          viewBox="0 0 10 8"
                          fill="none"
                        >
                          <path
                            d="M1 4L3.5 6.5L9 1"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>

                    {/* Col 1: Badge + Date stacked, centered */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 4,
                        paddingTop: 3,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '2px 6px',
                          borderRadius: 10,
                          backgroundColor:
                            CATEGORY_STYLES[memory.category]?.bg ??
                            'var(--streamlain-hover-color)',
                          color:
                            CATEGORY_STYLES[memory.category]?.fg ??
                            'var(--streamlain-text-primary-color)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {CATEGORY_LABELS[memory.category]?.[lang] ??
                          memory.category}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          color: 'var(--streamlain-text-secondary-color)',
                        }}
                      >
                        {new Date(memory.createdAt).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Col 2: Content */}
                    <div style={{ minWidth: 0, paddingTop: 2 }}>
                      <div
                        style={{
                          fontSize: 13,
                          lineHeight: '20px',
                          color: 'var(--streamlain-text-primary-color)',
                        }}
                      >
                        {memory.content}
                      </div>
                    </div>

                    {/* Col 3: Buttons + Importance stacked, centered */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <div style={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="plain"
                          style={{
                            padding: 4,
                            color: 'var(--streamlain-text-secondary-color)',
                          }}
                          title={lang === 'de' ? 'Bearbeiten' : 'Edit'}
                          onClick={() => openEditDialog(memory)}
                        >
                          <EditIcon style={{ width: 16, height: 16 }} />
                        </Button>
                        <Button
                          variant="plain"
                          style={{
                            padding: 4,
                            color: memory.pinned
                              ? '#f59e0b'
                              : 'var(--streamlain-text-secondary-color)',
                          }}
                          title={
                            memory.pinned
                              ? lang === 'de'
                                ? 'Loslassen'
                                : 'Unpin'
                              : lang === 'de'
                                ? 'Anpinnen (geschuetzt vor Auto-Bereinigung)'
                                : 'Pin (immune to auto-cleanup)'
                          }
                          onClick={() =>
                            void handlePin(memory.id, !memory.pinned)
                          }
                        >
                          {memory.pinned ? (
                            <FavoritedIcon style={{ width: 16, height: 16 }} />
                          ) : (
                            <FavoriteIcon style={{ width: 16, height: 16 }} />
                          )}
                        </Button>
                        <Button
                          variant="plain"
                          style={{
                            padding: 4,
                            color: 'var(--streamlain-text-secondary-color)',
                          }}
                          onClick={() => handleDelete(memory.id)}
                        >
                          <DeleteIcon style={{ width: 16, height: 16 }} />
                        </Button>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                        }}
                        title={
                          lang === 'de'
                            ? `Effektive Wichtigkeit: ${effImpRounded}/10 (Basis: ${memory.importance}/10). Sinkt ueber Zeit wenn nicht abgerufen.`
                            : `Effective importance: ${effImpRounded}/10 (base: ${memory.importance}/10). Decays over time if not recalled.`
                        }
                      >
                        {Array.from({ length: 10 }, (_, i) => (
                          <div
                            key={i}
                            style={{
                              width: 3,
                              height: 8,
                              borderRadius: 1,
                              backgroundColor:
                                i < Math.round(effImp)
                                  ? memory.pinned
                                    ? '#f59e0b'
                                    : '#3b82f6'
                                  : 'var(--streamlain-hover-color)',
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </SettingWrapper>
        </>
      ) : null}
    </>
  );
};
